import {computed, inject, Injectable, signal} from '@angular/core';
import {
  CreateTimesheetRequest,
  DocumentSummary,
  TimesheetDetail,
  TimesheetService,
  TimesheetSummary,
  UpdateTimesheetRequest,
  UpdateTimesheetStateRequest,
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

@Injectable({providedIn: 'root'})
export class TimesheetStoreService {
  private readonly timesheetService = inject(TimesheetService);
  private readonly queryClient = inject(QueryClient);

  filterParams = signal<TimesheetFilter>({} as TimesheetFilter);
  selectedTimesheetId = signal<number | null>(null);

  selectedTimesheetQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEET, this.selectedTimesheetId()],
    queryFn: () => lastValueFrom(this.timesheetService.getTimesheetById(this.selectedTimesheetId()!)),
    enabled: this.selectedTimesheetId() !== null,
  }));
  selectedTimesheet = computed<TimesheetDetail | null>(() => this.selectedTimesheetQuery.data() ?? null);
  isLoadingSelectedTimesheet = computed(() => this.selectedTimesheetQuery.isLoading());

  selectedTimesheetDocumentsQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEET_DOCUMENTS, this.selectedTimesheetId()],
    queryFn: () => lastValueFrom(this.timesheetService.getTimesheetDocuments(this.selectedTimesheetId()!)),
    enabled: this.selectedTimesheetId() !== null,
  }));
  selectedTimesheetDocuments = computed<DocumentSummary[]>(() => this.selectedTimesheetDocumentsQuery.data()?.documents ?? []);

  private get _hasDates() {
    const {startDate, endDate} = this.filterParams();
    return startDate != null && endDate != null;
  }

  timesheetsQuery = injectQuery(() => ({
    queryKey: [QueryKey.TIMESHEETS, this.filterParams()],
    queryFn: () => lastValueFrom(this.timesheetService.getTimesheets(
      this.filterParams().concernIds ?? [],
      this.filterParams().createdByIds ?? [],
      this.filterParams().timesheetTypeIds ?? [],
      this.filterParams().statusIds ?? [],
      [],
      this.filterParams().searchTerm,
      this.filterParams().isBillable ?? null,
      null,
      this.filterParams().startDate,
      this.filterParams().endDate)
    ),
    enabled: this._hasDates,
  }));
  timesheets = computed<TimesheetSummary[]>(() => this.timesheetsQuery.data()?.timesheets ?? []);
  isLoading = computed(() => this.timesheetsQuery.isLoading());

  createTimesheetMutation = injectMutation(() => ({
    mutationFn: (request: CreateTimesheetRequest) =>
      lastValueFrom(this.timesheetService.createTimesheet(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEETS]});
    },
  }));

  updateTimesheetMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateTimesheetRequest }) =>
      lastValueFrom(this.timesheetService.updateTimesheet(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEETS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEET, request.id]});
    },
  }));

  deleteTimesheetMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.timesheetService.deleteTimesheet(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEETS]});
    },
  }));

  updateTimesheetStateMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateTimesheetStateRequest }) =>
      lastValueFrom(this.timesheetService.updateTimesheetState(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEETS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEET, request.id]});
    },
  }));

  attachTimesheetDocumentMutation = injectMutation(() => ({
    mutationFn: (req: { timesheetId: number; file: File }) =>
      lastValueFrom(this.timesheetService.attachTimesheetDocument(req.timesheetId, req.file)),
    onSuccess: (_, req) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEET_DOCUMENTS, req.timesheetId]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]});
    },
  }));

  deleteTimesheetDocumentMutation = injectMutation(() => ({
    mutationFn: (req: { timesheetId: number; documentId: number }) =>
      lastValueFrom(this.timesheetService.deleteTimesheetDocument(req.timesheetId, req.documentId)),
    onSuccess: (_, req) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TIMESHEET_DOCUMENTS, req.timesheetId]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]});
    },
  }));

  async saveTimesheetDocuments(params: {
    timesheetId: number;
    pendingFiles: File[];
    pendingDeletionIds: number[];
  }): Promise<void> {
    const {timesheetId, pendingFiles, pendingDeletionIds} = params;
    await Promise.all([
      ...pendingFiles.map(file =>
        this.attachTimesheetDocumentMutation.mutateAsync({timesheetId, file})
      ),
      ...pendingDeletionIds.map(documentId =>
        this.deleteTimesheetDocumentMutation.mutateAsync({timesheetId, documentId})
      ),
    ]);
  }
}

export interface TimesheetFilter {
  searchTerm: string | null;
  concernIds: number[] | null;
  createdByIds: number[] | null;
  timesheetTypeIds: number[] | null;
  statusIds: number[] | null;
  isBillable: boolean | null;
  startDate: Date;
  endDate: Date;
}
